<script>
import { escapeHtml } from '../helpers';
import { mapGetters } from 'vuex';
import QuestionStatement from '../components/QuestionStatement';
import QuestionCard from '../components/QuestionCard';

export default {
  name: 'QuestionDetail',
  components: {
    QuestionStatement,
    QuestionCard,
  },
  metaInfo() {
    if (this.question) {
      let meta = [
        {
          vmid: 'og:url',
          property: 'og:url',
          content:
            this.$t('global.url') +
            '/' +
            this.$t('questionCard.linkBase') +
            '/' +
            this.questionSlug
        }
      ];
      if (this.question.description) {
        meta.push({
          vmid: 'description',
          name: 'description',
          content: this.question.description
        });
        meta.push({
          vmid: 'og:description',
          property: 'og:description',
          content: this.question.description
        });
      }
      if (this.question && this.question.title)
        meta.push({
          vmid: 'og:title',
          property: 'og:title',
          content: escapeHtml(this.question.title.rendered)
        });
      if (this.question && this.question.preview_img)
        meta.push({
          vmid: 'og:image',
          property: 'og:image',
          content: this.question.preview_img
        });

      return {
        titleTemplate: this.question.seo_title
          ? this.question.seo_title
          : escapeHtml(this.question.title.rendered),
        link: [
          {
            vmid: 'canonical',
            rel: 'canonical',
            href:
              this.$t('global.url') +
              '/' +
              this.$t('questionCard.linkBase') +
              '/' +
              this.questionSlug
          }
        ],
        meta: meta
      };
    }
  },

  mounted() {
    this.init(this.$route.params.slug);
  },
  methods: {
    init(slug) {
      if (slug) this.$store.dispatch('selectQuestion', slug);
    },
    scrollToTop() {
      this.$scrollTo('#questionCloud', 950, { easing: [0.165, 0.84, 0.44, 1] });
    }
  },
  computed: {
    ...mapGetters({
      question: 'getSelectedQuestion',
      isLoading: 'getQuestionsIsLoading'
    }),
    relatedQuestions() {
      return this.$store.getters.getRelatedQuestions(
        this.question.id,
        this.question.related_questions
      );
    },
    questionSlug() {
      return this.$route.params.slug;
    },
    baseUrl() {
      return this.$route.path.split('/')[1];
    }
  },
  beforeRouteUpdate(to, from, next) {
    this.$scrollTo('#questionDetail', 950, { easing: [0.165, 0.84, 0.44, 1] });
    next();
  },
  beforeDestroy() {
    this.$store.dispatch('selectQuestion', null);
  }
};
</script>

<template>
  <section
    id="questionDetail"
    class="question-detail"
    v-bind:class="{ 'question-detail--no-conent': !question }"
  >
    <div class="question-detail__content" v-if="!isLoading && question">
      <!-- title -->
      <div class="question-detail__title-wrapper">
        <router-link
          v-bind:to="'/' + $t('questionCard.linkBase')"
          class="question-detail__link-overview"
          v-if="baseUrl === $t('questionCard.linkBase')"
        ></router-link>
        <a
          v-on:click="scrollToTop()"
          class="question-detail__link-cloud"
          v-if="baseUrl === 'home'"
        ></a>
        <h1
          class="question-detail__title page-title"
          v-html="question.title.rendered"
        ></h1>
      </div>

      <!-- statements -->
      <div v-for="(statement, i) in question.statements" v-bind:key="i">
        <question-statement
          v-bind:content="statement.content"
          v-bind:contributor="statement.contributor"
        >
        </question-statement>
      </div>

      <!-- related questions -->
      <section class="related-questions">
        <header class="related-questions__header">
          <h1 class="related-questions__header-title">
            {{ $t('questionPage.relatedQuestions') }}
          </h1>
          <router-link
            v-bind:to="'/' + $t('questionCard.linkBase')"
            class="related-questions__header-overview read-more"
            >{{ $t('questionPage.allQuestions') }}</router-link
          >
        </header>
        <question-card
          v-for="(question, i) in relatedQuestions"
          v-bind:key="i"
          v-bind:title="question.title.rendered"
          v-bind:slug="question.slug"
          v-bind:categories="question.question_categories"
        >
        </question-card>
      </section>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.question-detail {
  @include container;
  @include page;
}
.question-detail--no-conent {
  padding: 0;
}
.question-detail__title-wrapper {
  position: relative;
}
.question-detail__title {
  max-width: none;
  margin-left: 11.52rem;
  margin-bottom: 16rem;

  @include breakpoint(sm-down) {
    margin-left: 0;
    margin-bottom: 9.6rem;
    margin-top: 4rem;
  }
}
.question-detail__link-overview,
.question-detail__link-cloud {
  position: absolute;
  display: block;
  top: -0.96rem;
  left: 0.16rem;
  width: 5.6rem;
  height: 4.8rem;
  margin: 0;
  padding: 1.28rem 0;
  background: center url('~@/assets/images/ico-back-overview.svg') no-repeat
    transparent;
  cursor: pointer;
  z-index: 1;
  transition: transform 200ms;

  @include breakpoint('sm-down') {
    top: -5.12rem;
    left: 0rem;
    width: 3.36rem;
    height: 2.08rem;
    background-size: 3.36rem;
  }

  @include breakpoint('md-up') {
    &:hover {
      transform: translateX(-0.8rem);
    }
  }
}
.question-detail__link-cloud {
  transform: rotate(90deg) translate(0.24rem, 0.32rem);

  @include breakpoint('sm-down') {
    transform: rotate(90deg) translate(-0.32rem, 0.32rem);
  }

  @include breakpoint('md-up') {
    &:hover {
      transform: rotate(90deg) translate(-0.72rem, 0.32rem);
    }
  }
}
.question-statement {
  margin-bottom: 20rem;

  @include breakpoint(sm-down) {
    margin-bottom: 12.8rem;
  }
}
.related-questions {
  @include row;
  margin-bottom: 16rem;

  @include breakpoint(sm-down) {
    margin-bottom: 0;
  }
}
.related-questions__header {
  @include col;
  display: flex;
  width: calc(100% - #{$layout-gutter * 2});
  min-width: calc(100% - #{$layout-gutter * 2});
  justify-content: space-between;
  padding-bottom: 1.1rem;
  border-bottom: 0.1rem solid rgba($color-primary, 0.16);
}
.related-questions__header-title {
  font-size: 1.8rem;
  margin: 0;
}
.related-questions__header-overview {
  margin-top: 0.8rem;
}
.question-card {
  @include col;
  width: calc(50% - 3.2rem);

  @include breakpoint('xs-down') {
    width: 100%;
  }
}
</style>
